import React, { useCallback, useState } from 'react';

import { useAuth } from '@fastlessjs/backoffice-layer/dist/hooks';
import debug from '@fastlessjs/backoffice-layer/dist/utils/debug';
import { Badge, Button, Modal } from 'antd';

import { OrdersReturnRequestTable } from './OrdersReturnRequestTable';
import { t } from '../../config/translations';
import { useCreateReturn } from '../../returns/hooks';
import { buildReturnPayloadFromCartKey } from '../../returns/utils/buildReturnPayload';
import { useReturnStore } from '../../store';

// Register this component for debug tracking
debug.registerComponent('OrderReturnModal');

export type OrderReturnModalProps = {
  handleSubmit: (orderId: string, clientId?: string) => void;
};

const OrderReturnModal = (props: OrderReturnModalProps) => {
  debug.trackRender('OrderReturnModal');
  const { handleSubmit } = props;
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const { user } = useAuth();
  const { mutateAsync, isPending } = useCreateReturn({
    headers: { 'x-tenant-id': user.id },
  });

  const { returnOrderLines, isSubmittingReturn } = useReturnStore();
  const isSubmitLocked = isSubmittingReturn || isPending;
  debug.log(
    'OrderReturnModal',
    `Return order lines count: ${Object.keys(returnOrderLines).length}`,
  );

  const handleOpenModal = () => {
    debug.log('OrderReturnModal', `Opening modal`);
    setIsModalOpen(true);
  };

  const handleOk = () => {
    setIsModalOpen(false);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const handleSubmitAndClose = (orderId: string, clientId?: string) => {
    handleSubmit(orderId, clientId);
    setIsModalOpen(false);
  };

  const handleAskReturns = useCallback(async () => {
    if (isSubmitLocked) {
      return;
    }

    debug.log(
      'OrderReturnModal',
      `Submitting all returns - count: ${Object.keys(returnOrderLines).length}`,
    );

    const cartKeys = Object.keys(returnOrderLines);
    try {
      await cartKeys.reduce(async (previousSubmission, key) => {
        await previousSubmission;
        const payload = buildReturnPayloadFromCartKey(returnOrderLines, key);
        if (payload) {
          await mutateAsync(payload);
        }
      }, Promise.resolve());
    } catch (error) {
      debug.error('OrderReturnModal', `Return submission failed: ${error}`);
    }
  }, [isSubmitLocked, mutateAsync, returnOrderLines]);

  return (
    <div>
      {(Object.keys(returnOrderLines)?.length > 0 || isModalOpen) && (
        <>
          <Badge count={Object.keys(returnOrderLines)?.length}>
            <Button type="primary" onClick={handleOpenModal} disabled={isSubmitLocked}>
              {t('button.myReturnRequests')}
            </Button>
          </Badge>
          <Modal
            title={t('modal.returnRequestsByOrder')}
            open={isModalOpen}
            onOk={handleOk}
            onCancel={handleCancel}
            width="auto"
            bodyStyle={{ padding: '12px' }}
            footer={[
              <Button key="cancel" onClick={handleCancel} disabled={isSubmitLocked}>
                {t('button.back')}
              </Button>,
              <Button
                key="submit"
                type="primary"
                style={{ marginTop: '12px', marginRight: '24px' }}
                onClick={handleAskReturns}
                loading={isSubmitLocked}
                disabled={isSubmitLocked}
              >
                {t('button.submitAllReturns')}
              </Button>,
            ]}
          >
            <OrdersReturnRequestTable
              returnOrderLines={returnOrderLines}
              mutateAsync={mutateAsync}
              isSubmitLocked={isSubmitLocked}
              onEdit={handleSubmitAndClose}
            />
          </Modal>
        </>
      )}
    </div>
  );
};

export { OrderReturnModal };

// Mark this component as initialized
debug.markInitialized('OrderReturnModal');
